/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, computed, effect, input, output, viewChild } from "@angular/core";
import { ChartConfiguration } from "chart.js";
import { BaseChartDirective } from "ng2-charts";

import { AdaaHelper } from "../../../../core/utils";
import { Constants } from "../../../constants/constants";
import type { PillarPerformanceModelType } from "../../../models";

/* Ring geometry, shared with the tooltip anchor in the parent: the arc centre
   line sits halfway between the cutout and the outer edge. */
export const PILLAR_RING = {
  cutout: 0.69,
  padding: 5,
};

@Component({
  selector: "adaa-pillar-performance-graph",
  imports: [BaseChartDirective],
  styleUrl: "./government-enabler-pillars.scss",
  template: `<canvas
    class="pillar-performance-chart"
    [id]="chartId()"
    baseChart
    type="doughnut"
    [datasets]="datasets()"
    [labels]="labels()"
    [options]="options()"
    (chartClick)="clicked($event)"
  ></canvas>`,
})
export class PillarPerformanceGraphComponent {
  data = input.required<PillarPerformanceModelType>();
  chartId = input.required<string>();
  activeEkpiId = input<number | null>(null);

  segmentHovered = output<number | null>();
  segmentClicked = output<number>();

  isEmpty = computed(() => this.data().ekpiList.length === 0);

  datasets = computed(() => {
    if (this.isEmpty()) {
      return [
        {
          backgroundColor: [Constants.COLOR_CODES[Constants.CONSTANT_COLORS.EVALUATION_LIGHTGRAY]],
          borderWidth: 0,
          data: [100],
          datalabels: { display: false },
        } as any,
      ];
    }

    const ekpiList = this.data().ekpiList;

    return [
      {
        backgroundColor: ekpiList.map((ekpi) => this.getColor(ekpi.evaluation)),
        borderColor: "#FFFFFF",
        borderWidth: ekpiList.length > 1 ? 2 : 0,
        hoverOffset: 5,
        data: ekpiList.map(() => 100 / ekpiList.length),
        datalabels: { display: false },
        ekpiIds: ekpiList.map((ekpi) => ekpi.id),
      } as any,
    ];
  });

  labels = computed(() => this.data().ekpiList.map((ekpi) => AdaaHelper.getItemValueByToken(ekpi, "ekpiName")));

  options = computed(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      cutout: `${PILLAR_RING.cutout * 100}%`,
      /* room for hoverOffset to push a segment out without the canvas clipping it */
      layout: { padding: PILLAR_RING.padding },
      plugins: {
        datalabels: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
        legend: {
          display: false,
        },
      },
      onHover: (event: any, elements) => {
        const hoverable = !this.isEmpty() && elements.length > 0;

        if (event?.native?.target) {
          event.native.target.style.cursor = hoverable ? "pointer" : "default";
        }

        const ekpiId = hoverable ? (this.data().ekpiList[elements[0].index]?.id ?? null) : null;
        if (ekpiId !== this.activeEkpiId()) this.segmentHovered.emit(ekpiId);
      },
    } as ChartConfiguration<"doughnut">["options"];
  });

  chart = viewChild<BaseChartDirective>(BaseChartDirective);

  private readonly _syncActiveSegment = effect(() => {
    const activeEkpiId = this.activeEkpiId();
    const chart = this.chart()?.chart;
    if (!chart) return;

    const index = this.data().ekpiList.findIndex(({ id }) => id === activeEkpiId);
    chart.setActiveElements(index === -1 ? [] : [{ datasetIndex: 0, index }]);
    chart.update();
  });

  public clicked(event: any) {
    if (this.isEmpty()) return;
    if (!event.active?.length) return;

    const id = this.data().ekpiList[event.active[0].index]?.id;
    if (id) this.segmentClicked.emit(id);
  }

  public getColor(code?: number) {
    return Constants.COLOR_CODES[code ?? Constants.CONSTANT_COLORS.EVALUATION_GRAY];
  }
}
